'use client';

import {
  forwardRef,
  type CSSProperties,
  type HTMLAttributes,
  type ReactNode,
} from 'react';
import { Box, HStack } from '@terra/ui-styled-system';
import { ArrowDown01Icon, ArrowRight01Icon } from '@terra/ui-icons';
import { BodyDefault, MetaDefault } from '../../../../components/typography';
import {
  CHAT_STEP_GAP,
  CHAT_STEP_LABEL_COLOR,
  CHAT_STEP_MIN_HEIGHT,
} from '../chat-nesting-layout';
import { getChatTextShimmerStyle } from '../chat-text-shimmer';
import type { TraceStatus } from './trace-types';

type TraceLineProps = Omit<HTMLAttributes<HTMLDivElement>, 'title'> & {
  title: string;
  description?: string;
  status?: TraceStatus;
  icon?: ReactNode;
  canExpand?: boolean;
  isExpanded?: boolean;
  titleStyle?: CSSProperties;
  meta?: ReactNode;
  trailing?: ReactNode;
};

export const TraceLine = forwardRef<HTMLDivElement, TraceLineProps>(
  function TraceLine(
    {
      title,
      description,
      status,
      icon,
      canExpand,
      isExpanded,
      titleStyle,
      meta,
      trailing,
      ...rest
    },
    ref,
  ) {
    const isRunning = status === 'running';
    const titleColor =
      status === 'error'
        ? 'fg.error'
        : status === 'pending'
          ? 'fg.disabled'
          : CHAT_STEP_LABEL_COLOR;

    return (
      <HStack
        ref={ref}
        gap={CHAT_STEP_GAP}
        alignItems="center"
        minHeight={CHAT_STEP_MIN_HEIGHT}
        cursor={canExpand ? 'pointer' : 'default'}
        _hover={canExpand ? { color: 'fg.default' } : undefined}
        color={CHAT_STEP_LABEL_COLOR}
        width="100%"
        minW={0}
        overflow="hidden"
        pr="16px"
        data-status={status}
        aria-busy={isRunning || undefined}
        {...rest}
      >
        {icon ? (
          <Box display="flex" flexShrink={0}>
            {icon}
          </Box>
        ) : null}
        <BodyDefault
          as="span"
          title={title}
          color={titleColor}
          fontWeight="400"
          overflow="hidden"
          textOverflow="ellipsis"
          whiteSpace="nowrap"
          flexShrink={1}
          minW={0}
          m={0}
          animationStyle={isRunning ? 'shimmer' : undefined}
          style={
            isRunning
              ? { ...getChatTextShimmerStyle(true), ...titleStyle }
              : titleStyle
          }
        >
          {title}
        </BodyDefault>
        {description ? (
          <MetaDefault
            color="fg.muted"
            minW={0}
            flexShrink={1}
            overflow="hidden"
            textOverflow="ellipsis"
            whiteSpace="nowrap"
            m={0}
          >
            {description}
          </MetaDefault>
        ) : null}
        {meta}
        {trailing}
        {canExpand ? (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            flexShrink={0}
            fontSize="10px"
          >
            {isExpanded ? (
              <ArrowDown01Icon size={10} />
            ) : (
              <ArrowRight01Icon size={10} />
            )}
          </Box>
        ) : null}
      </HStack>
    );
  },
);
